import {Axis, NumberValue} from 'd3';
import * as d3 from 'd3';
import {ScaleLinear} from 'd3';
import _ from 'underscore';

import {VisibilityOptions} from 'components/VisibilityOptions';
import {SliceMapType} from 'constants/MapsConstants';
import Component, {ComponentOptions} from 'molbio/maps/linear/Component';
import {Axis as AxisLayout} from 'molbio/maps/linear/LinearLayout';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import Range from 'molbio/maps/selectables/Range';
import Type from 'molbio/maps/Type';
import * as sequtil from 'molbio/SeqUtil';
import {TabState} from 'Navigation';
import throttle from 'util/throttle';

export interface PolymerAxisViewOptions extends ComponentOptions {
  visibilityOptions: VisibilityOptions;
  slice: Slice;
  range: Range | null;
  parent: SliceView;
  mapType: SliceMapType;
}

// Used to render both Sequence and Protein axis views
abstract class PolymerAxisView extends Component {
  tabstate!: TabState;
  mapType!: SliceMapType;
  parent!: SliceView;
  range!: Range | null;
  slice!: Slice;
  layout!: AxisLayout;
  axis!: Axis<NumberValue>;

  initialize(options: PolymerAxisViewOptions): void {
    super.initialize(options);
    this.tabstate = options.tabstate;
    this.mapType = options.mapType;
    this.parent = options.parent;
    this.range = options.range;
    this.slice = options.slice;
    this.layout = this.model.get('layout').Axis;
    this.axis = d3.axisBottom(this.getScale()).tickSizeOuter(0).tickSizeInner(8);
    d3.select(this.el).select('g').call(this.axis);
  }

  abstract getBasesType(): Type;

  getStartIndex(): number {
    return this.model.get('sequence').startIndex();
  }

  getPolymerLength(): number {
    return this.model.get('sequence').length();
  }

  registerListeners(): void {
    super.registerListeners();
    if (this.range) {
      this.listenTo(
        this.range,
        'rangeChange',
        throttle((redrawOnResize, redrawOnPan) => {
          if (redrawOnResize || redrawOnPan) {
            this.update(false);
          }
        }, 50)
      );
    }
  }

  setTicks(): void {
    let targetPerVisible;
    const startIndex = this.getStartIndex();
    if (
      this.mapType === 'minimap' &&
      sequtil.modelToDisplay(this.slice.get('rightBP')!, startIndex) > 1000000
    ) {
      targetPerVisible = 4;
    } else if (this.mapType === 'linear' && sequtil.modelToDisplay(this.slice.end(), startIndex) > 10000000) {
      targetPerVisible = 4;
    } else {
      targetPerVisible = 8;
    }

    let ticks = Math.ceil((targetPerVisible * this.slice.get('width')!) / this.slice.get('visibleWidth')!);
    ticks = Math.min(this.getPolymerLength(), ticks);
    this.axis.ticks(ticks);
  }

  /**
   * Figure out which ticks to display on this slice.
   * @param domain A tuple of [start, end]; what indices of the sequence the slice starts & ends at.
   * @param m The number of ticks to display.
   */
  tickFunction(domain: Array<number>, m: number): Array<number> {
    // ported from d3.js
    /**
     * Given an ordered domain of numerical values, enforces an invariant that the smallest value comes first
     * @param domain An ordered list of numbers.
     */
    function scaleExtent(domain: Array<number>): [number, number] {
      const start = domain[0];
      const stop = domain[domain.length - 1];
      if (start < stop) {
        return [start, stop];
      } else {
        return [stop, start];
      }
    }

    // If we're zoomed out we don't want to show a tick every 10 residues; do a logarithmic backoff instead
    const [start, end] = scaleExtent(domain);
    const span = end - start;
    // span / m is the 'exact', float-y step.
    // 10 ^ floor(ln(exactStep) / ln(10)) = step
    // log10(step) = floor(ln(exactStep) / ln(10))
    // log10(step) == floor(log10(exactStep))
    // so step is the largest exponent to which 10 can be raised and not exceed span / m.
    let step = Math.pow(10, Math.floor(Math.log(span / m) / Math.LN10));
    // How far off were we? What is the ratio of step to exactStep?
    const err = (m / span) * step;
    if (err <= 0.15) {
      step *= 10;
    } else if (err <= 0.35) {
      step *= 5;
    } else if (err <= 0.75) {
      step *= 2;
    }

    // First tick we should show - first multiple of step after start
    const steppedStart = Math.ceil(start / step) * step;
    // Last tick we should show - last multiple of step before end
    const steppedEnd = Math.floor(end / step) * step + step * 0.5;

    let restrictedDomain = {
      leftBP: this.slice.get('leftBP'),
      rightBP: this.slice.get('rightBP'),
    };
    if (this.range != null) {
      restrictedDomain = {
        leftBP: Math.floor(this.range.start()),
        rightBP: Math.floor(this.range.end()),
      };
    }
    // Adjust for display value if e.g. we're starting at 200000 because we're in a chromosome import.
    // 'Normal' start index is 1 which makes this a noop.
    restrictedDomain.leftBP! += this.getStartIndex() - 1;
    restrictedDomain.rightBP! += this.getStartIndex() - 1;

    // CUSTOM: restrict to our visible range.
    const adj1 = Math.max(0, Math.floor((restrictedDomain.leftBP! - steppedStart) / step) - 2); // safety net of -2
    const visibleStart = steppedStart + adj1 * step;
    const adj2 = Math.max(0, Math.ceil((steppedEnd - restrictedDomain.rightBP!) / step) - 2); // safety net of -2
    const visibleEnd = steppedEnd - adj2 * step;

    return d3.range(visibleStart, visibleEnd, step);
  }

  /**
   * Create a D3 Linear Scale that maps from display index to pixel offset.
   */
  getScale(): ScaleLinear<number, number> {
    // Contains 'index' values for this slice
    const scale = this.slice.x().copy();
    // Get display values for endpoints
    let domain = scale.domain().map((x) => sequtil.modelToDisplay(x, this.getStartIndex()));
    scale.domain(domain);

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const originalTicks = scale.ticks;

    const ref = this;
    const {mapType} = this;

    scale.ticks = function () {
      let ticks;
      if (mapType === 'minimap' || ref.range != null) {
        // @ts-expect-error TS2556: A spread argument must either have a tuple type or be passed to a rest parameter
        // eslint-disable-next-line prefer-rest-params
        ticks = ref.tickFunction(this.domain(), ...arguments);
      } else {
        // eslint-disable-next-line prefer-rest-params
        ticks = originalTicks.apply(this, arguments);
      }

      domain = this.domain();
      if (ticks.length > 0 && ticks[ticks.length - 1] === domain[domain.length - 1]) {
        ticks.pop();
      }
      if (ticks.length === 0) {
        ticks.push(ref.getPolymerLength());
      }
      return ticks;
    };

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const originalCopy = scale.copy;
    scale.copy = function () {
      // eslint-disable-next-line prefer-rest-params
      const copy = originalCopy.apply(this, arguments);
      // eslint-disable-next-line @typescript-eslint/unbound-method
      copy.ticks = this.ticks;
      return copy;
    };

    return scale;
  }

  setScale(): void {
    this.axis.scale(this.getScale());
  }

  _performAdditionalUpdates(): void {
    // Children can implement specific update functionality here
  }

  getTypeOffset(): number {
    return this.getBasesType().charWidth() / 2;
  }

  getAxisRoot(): d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown> {
    return d3.select(this.el).select('g');
  }

  update(trigger: boolean): void {
    let excess;
    let spaceBetweenLabels;
    let width;
    if (trigger == null) {
      trigger = true;
    }
    if (!this.shouldShow()) {
      return;
    }

    if (this.getPolymerLength() === 0) {
      this.$el.hide();
      return;
    }

    this.$el.show();

    this.setScale();
    this.setTicks();

    const root = this.getAxisRoot();
    root.call(this.axis);
    const tickValues = root.selectAll('g').data();
    const minTick = _.min(tickValues);
    const maxTick = _.max(tickValues);
    const first = root.selectAll('g').filter((d) => d === minTick);
    const last = root.selectAll('g').filter((d) => d === maxTick);

    const typeOffset = this.getTypeOffset();
    root.attr('transform', `translate(${typeOffset},${this.layout.vMargin()})`);
    root.select('path').attr('transform', `translate(${-typeOffset},0)`);

    root.selectAll('.linear-axis-piece').remove();

    const spacing = 3;
    const re = /[0-9]+/;
    const lastNode = last.node();
    if (lastNode != null) {
      spaceBetweenLabels = 70; // approximate space between labels.

      // Pixels to the right of the last label
      const matches = last.attr('transform').match(re);
      const cutoff = this.slice.get('width')! - parseInt(matches![0], 10);

      // Width of half the text
      // @ts-expect-error typing on lastNode still unclear; not guaranteed to have textContent
      width = this.layout.get('type').width(lastNode.textContent.length / 2 + 1);

      excess = width - cutoff;
      if (excess >= 0) {
        last.select('text').attr('style', '');
        last.select('text').attr('text-anchor', 'end');

        if (spaceBetweenLabels < 2 * width + excess + spacing) {
          // label + label + excess + space
          last.select('text').text('');
        }
      }
    }

    const firstNode = first.node();
    if (firstNode != null) {
      const matches = first.attr('transform').match(re);
      const transform = parseInt(matches![0], 10); // pixels to the left of the first label
      // @ts-expect-error typing on firstNode still unclear; not guaranteed to have textContent
      width = this.layout.get('type').width(firstNode.textContent.length / 2 + 1); // width of half the text
      if (width >= transform) {
        const match = /translate\((.+)[\s,)]/.exec(first.attr('transform'));
        if (match != null) {
          const parentXStr = match[1];
          const counterX = -parseFloat(parentXStr);

          first.select('text').attr('style', '');
          first.select('text').attr('text-anchor', 'start');
          first.select('text').attr('x', counterX);
          first.select('text').attr('font-size', 10);
        }
      }

      excess = width - transform;
      // @ts-expect-error spaceBetweenLabels not guaranteed to be set in previous conditional block
      if (spaceBetweenLabels < 2 * width + excess + spacing) {
        // If the element was shifted over too much, it might overlap the previous axis lable.
        first.select('text').text('');
      }
    }

    d3.select(this.el).selectAll('.axis .tick line').style('stroke-width', 1).style('stroke', 'black');

    // unset d3 default styles
    d3.select(this.el).select('.component-wrapper').attr('font-family', null).attr('font-size', null);

    this._performAdditionalUpdates();

    if (trigger) {
      this.trigger('redraw');
    }
  }
}

export default PolymerAxisView;
